import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ReloadIcon } from "@radix-ui/react-icons";
import { Link, useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { signUp } from "aws-amplify/auth";

const schema = yup
  .object({
    email: yup
      .string()
      .email("Invalid email address")
      .required("Email address is required."),
    password: yup
      .string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required."),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("password"), null], "Passwords must match")
      .required("Please confirm your password."),
  })
  .required();

const SignUp = () => {
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [email, setEmail] = useState("");

  const {
    register,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      const email = data.email.toLowerCase();
      setEmail(email);

      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: email,
        password: data.password,
        options: {
          userAttributes: {
            email: email,
          },
        },
      });

      if (isSignUpComplete) {
        // User is automatically signed in
        history.push("/home");
      } else {
        // Show confirmation step
        setShowConfirmation(true);
      }
    } catch (error) {
      console.log("Sign-up error:", error);
      setError("root", {
        message: error.message || "An error occurred during sign-up.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showConfirmation) {
    return (
      <div className="relative min-h-screen bg-zinc-700">
        <div
          className="fixed w-full h-full z-0 bg-cover bg-repeat bg-center"
          style={{ backgroundImage: `url(/bg-new.jpg)` }}
        />
        <div className="flex flex-col h-full items-center justify-center relative z-10">
          <Card className="p-8 max-w-md">
            <h2 className="text-2xl font-bold mb-4">Check Your Email</h2>
            <p className="mb-4">
              We've sent a confirmation code to <strong>{email}</strong>
            </p>
            <p className="mb-6">
              Please check your email and click the confirmation link to
              complete your registration.
            </p>
            <Button onClick={() => history.push("/login")} className="w-full">
              Back to Login
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-zinc-700">
      <div
        className="fixed w-full h-full z-0 bg-cover bg-repeat bg-center"
        style={{ backgroundImage: `url(/bg-new.jpg)` }}
      />
      <div className="flex flex-col h-full items-center justify-center relative z-10">
        <div className="mt-4 md:mt-8 lg:mt-16 mb-4 md:mb-8 lg:mb-12">
          <img
            alt="logo"
            src="/TriVista_Horizontal_Logo_White.png"
            className="w-[300px]"
          />
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="max-w-2xl flex flex-col items-center justify-center mx-8">
            <h1 className="text-3xl text-white text-center lg:tracking-[0.75rem] tracking-[0.5rem]">
              Create Account
            </h1>
            <Card className="mt-4 w-full mx-8 md:mx-auto md:w-auto px-4">
              {errors.root && (
                <Alert variant="destructive" className="mt-4 max-w-96">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{errors.root.message}</AlertDescription>
                </Alert>
              )}

              <div className="mt-4">
                <Label className="text-xl">Email</Label>
                <Input
                  className="h-8 w-80 sm:w-auto sm:min-w-80"
                  {...register("email")}
                />
                <div className="mt-1 flex gap-3 items-start">
                  {errors.email && (
                    <p className="text-red-500">{errors.email.message}</p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <Label className="text-xl">Password</Label>
                <Input
                  className="h-8 w-80 sm:w-auto sm:min-w-80"
                  type="password"
                  {...register("password")}
                />
                <div className="mt-1 flex gap-3 items-right">
                  {errors.password && (
                    <p className="text-red-500">{errors.password.message}</p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <Label className="text-xl">Confirm Password</Label>
                <Input
                  className="h-8 w-80 sm:w-auto sm:min-w-80"
                  type="password"
                  {...register("confirmPassword")}
                />
                <div className="mt-1 flex gap-3 items-right">
                  {errors.confirmPassword && (
                    <p className="text-red-500">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              </div>

              <Button
                className="my-4 w-full px-12 py-6 text-xl rounded-xl"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting && (
                  <ReloadIcon className="w-6 h-6 animate-spin mr-2" />
                )}
                {isSubmitting ? "Creating Account..." : "Create Account"}
              </Button>
            </Card>
          </div>
        </form>

        <div className="mt-4 text-white">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-300 hover:text-blue-200">
            Sign In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
